import React, { useContext, useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button, IconButton } from '@one-thd/sui-atomic-components';
import { Mic, Pause } from '@one-thd/sui-icons';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { stopListeningNow } from '../../AddToHelpers';

/**
 * TapToSpeak component for speech-to-text functionality
 */
export const TapToSpeak = ({
  onTranscriptChange,
  onListeningChange,
  onSpeechErrorChange,
  disabled,
  defaultText
}) => {
  // Speech recognition hook
  const {
    transcript,
    listening,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition();

  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  // Track previous transcript to avoid infinite loops
  const prevTranscriptRef = useRef('');
  const prevListeningRef = useRef(listening);
  const listeningRef = useRef(listening);
  const stopFallbackTimerRef = useRef(null);

  useEffect(() => {
    listeningRef.current = listening;

    if (!listening && stopFallbackTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(stopFallbackTimerRef.current);
      stopFallbackTimerRef.current = null;
    }
  }, [listening]);

  useEffect(() => {
    return () => {
      if (stopFallbackTimerRef.current && typeof window !== 'undefined') {
        window.clearTimeout(stopFallbackTimerRef.current);
      }

      if (listeningRef.current) {
        stopListeningNow();
      }
    };
  }, []);

  useEffect(() => {
    if (listening && disabled) {
      stopListeningNow();
    }
  }, [disabled, listening]);

  // Notify parent of listening state changes
  useEffect(() => {
    if (prevListeningRef.current === listening) {
      return;
    }

    prevListeningRef.current = listening;

    if (onListeningChange) {
      onListeningChange(listening);
    }
  }, [listening, onListeningChange]);

  // Update parent component when transcript changes
  useEffect(() => {
    // Only call onTranscriptChange if transcript has actually changed and is not empty
    if (transcript && transcript !== prevTranscriptRef.current && onTranscriptChange) {
      prevTranscriptRef.current = transcript;
      onTranscriptChange(transcript);
    }
  }, [transcript, onTranscriptChange]);

  const getSpeechUnavailableError = () => {
    if (!browserSupportsSpeechRecognition) {
      return 'Voice input is not supported in this browser. Please type your list.';
    }

    if (isMicrophoneAvailable === false) {
      return 'Microphone access is blocked. Allow microphone access and try again.';
    }

    return 'Voice input is currently unavailable. Please type your list.';
  };

  const startListeningWithFallback = async () => {
    try {
      await SpeechRecognition.startListening({
        continuous: true,
        language: 'en-US'
      });
    } catch {
      try {
        // Fallback: continuous: false is more broadly supported (e.g. older Safari/WebKit).
        // The user must tap again after each pause, but speech still works.
        await SpeechRecognition.startListening({
          continuous: false,
          language: 'en-US'
        });
      } catch {
        if (onSpeechErrorChange) {
          onSpeechErrorChange(getSpeechUnavailableError());
        }
      }
    }
  };

  const stopListeningWithFallback = () => {
    stopListeningNow();

    if (typeof window === 'undefined') {
      return;
    }

    if (stopFallbackTimerRef.current) {
      window.clearTimeout(stopFallbackTimerRef.current);
    }

    // Some browser implementations can get stuck in "listening" after stop.
    stopFallbackTimerRef.current = window.setTimeout(() => {
      if (listeningRef.current) {
        stopListeningNow();
      }
      stopFallbackTimerRef.current = null;
    }, 500);
  };

  const handleSpeechInput = () => {
    if (onSpeechErrorChange) {
      onSpeechErrorChange('');
    }

    if (listening) {
      stopListeningWithFallback();
      return;
    }

    if (!browserSupportsSpeechRecognition || isMicrophoneAvailable === false) {
      if (onSpeechErrorChange) {
        onSpeechErrorChange(getSpeechUnavailableError());
      }
      return;
    }

    // Keep click handling synchronous so the button does not enter a pending state.
    startListeningWithFallback();
  };

  const buttonText = listening ? 'Tap to pause' : defaultText;

  const buttonWrapper = classNames('sui-bg-primary', {
    'sui-w-64': !isMobile,
    'sui-border-2 sui-border-solid sui-rounded-lg': isMobile,
    'sui-border-primary': disabled && isMobile,
    'sui-border-accent': !disabled && isMobile,
    'sui-w-fit': isMobile,
  });

  if (!browserSupportsSpeechRecognition) {
    return null;
  }

  return (
    <div className={buttonWrapper}>
      {
        isMobile ? (
          <IconButton
            icon={listening ? Pause : Mic}
            onClick={handleSpeechInput}
            disabled={disabled}
            color="brand"
            buttonSize="normal"
            iconSize="regular"
            style={{ width: '4rem', height: '4rem' }}
          />
        ) : (
          <Button
            variant="secondary"
            endIcon={listening ? Pause : Mic}
            onClick={handleSpeechInput}
            disabled={disabled}
            fullWidth={!isMobile}
          >
            {
              !isMobile ? buttonText : (
                <span className="sui-border-primary sui-border-1" />
              )
            }
          </Button>
        )
      }
    </div>
  );
};

TapToSpeak.propTypes = {
  onTranscriptChange: PropTypes.func.isRequired,
  onListeningChange: PropTypes.func,
  onSpeechErrorChange: PropTypes.func,
  disabled: PropTypes.bool,
  defaultText: PropTypes.string
};

TapToSpeak.defaultProps = {
  disabled: false,
  defaultText: 'Tap to speak',
  onListeningChange: null,
  onSpeechErrorChange: null
};

export default TapToSpeak;
